import * as React from "react";
import { useLocation, useSearchParams } from "react-router";
import RisikenTabelleDataGrid from "../components/RisikenTabelleDataGrid";
import { useWindowManager } from "../components/windowManager/WindowManagerContext";
import { openRiskFormWindow } from "../components/windowManager/RiskFormWindow";

const numOrNull = (raw: unknown): number | null => {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Einstiegspunkt der Route `/risks/new`. Die Erfassung selbst ist kein
 * Seiteninhalt mehr, sondern eine Fenster-Instanz im Vollseiten-Modus — die
 * Route öffnet sie nur; darunter liegt die Risiken-Tabelle als Hintergrund,
 * auf dem der User nach dem Schließen der Vollseite landet.
 *
 * Parameter kommen aus der Query (`riskType`, `contactId`, `attachToContract`,
 * `editRiskId`) ODER aus `location.state` (Legacy-Aufrufer navigieren mit
 * State hierher). `?contactId=` wurde bis hierher still ignoriert.
 */
const NewRiskRoute: React.FC = () => {
  const { openWindow } = useWindowManager();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Die ID hängt an location.key: derselbe Aufruf (auch StrictMode-Doppel-
  // Effekt) trifft dieselbe Instanz, eine neue Navigation öffnet eine neue.
  const windowId = `risk-route-${location.key}`;

  // Bewusst auf location.key statt nur auf Mount: „Neues Risiko" aus Sidebar
  // oder ⌘K auf der bereits aktiven Route ist ein Same-Path-PUSH ohne Remount —
  // ohne diese Abhängigkeit passierte nach dem Schließen der Vollseite nie
  // wieder etwas.
  const state = location.state as Record<string, unknown> | null;
  React.useEffect(() => {
    const riskType =
      (typeof state?.riskType === "string" ? state.riskType : null) ??
      searchParams.get("riskType") ??
      undefined;
    const contactId =
      numOrNull(state?.kontaktId) ?? numOrNull(searchParams.get("contactId"));
    const contactName =
      typeof state?.kontaktName === "string" ? state.kontaktName : null;
    const attachToContractId =
      numOrNull(state?.vertragId) ?? numOrNull(searchParams.get("attachToContract"));
    const editRiskId = numOrNull(searchParams.get("editRiskId"));

    openRiskFormWindow(openWindow, {
      riskType,
      contactId,
      contactName,
      attachToContractId,
      editRiskId,
      // Edit-Fenster behalten ihre stabile `risk-edit-<id>`-ID.
      windowId: editRiskId ? undefined : windowId,
      mode: "page",
    });
    // searchParams/state werden absichtlich nur beim Öffnen gelesen — der
    // Effekt läuft je Navigation genau einmal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // Hintergrund der Route. Solange die Vollseite offen ist, verdeckt sie die
  // Tabelle vollständig (der Fenster-Body ist opak) — sichtbar wird sie erst
  // nach „Zurück"/Schließen, und dann ohne Nachladeruckler.
  return <RisikenTabelleDataGrid />;
};

export default NewRiskRoute;
